// src/pages/SignupPage.tsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  TextField,
  IconButton,
  InputAdornment,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import p4lLogo from "../assets/logo/PfL Logo no words 2023-11-21-01.png";
import topicsBackground from "../assets/images/topics_background.svg";
import Button from "../componentLibrary/Button";
import {
  validatePassword,
  validateEmail,
  validateUsername,
} from "../utils/auth";
import PasswordRequirementsWarning from "../components/PasswordRequirementsWarning";
import PrivacyPolicyModal from "../components/PrivacyPolicyModal";
import TermsOfServiceModal from "../components/TermsofServiceModal";
import EULAModal from "../components/EULAModal";
import { useSignUpUser } from "../api/mutations";
import { useGetLegalDates } from "../api/queries";
import { useNotification } from "../hooks/useNotification";

const SignupPage: React.FC = () => {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const { notify } = useNotification();
  const { data: legalDates } = useGetLegalDates();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeToLegal, setAgreeToLegal] = useState(false);
  const [error, setError] = useState("");
  const [showPasswordRequirements, setShowPasswordRequirements] =
    useState(false);
  const [emailAccountIssues, setEmailAccountIssues] = useState(true); // Pre-checked
  const [emailFeedbackRequests, setEmailFeedbackRequests] = useState(false);
  const [emailNewsUpdates, setEmailNewsUpdates] = useState(false);

  // Modal states
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showEULAModal, setShowEULAModal] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Auto-fill access code from URL query parameter
  useEffect(() => {
    const codeFromQuery = searchParams.get("code");

    if (codeFromQuery) {
      setAccessCode(codeFromQuery);
    }
  }, [searchParams]);

  // Helper function to render error message with proper formatting
  const renderErrorMessage = (errorMessage: string) => {
    const isAccessCodeLimitError = errorMessage.includes(
      "Redemption limit reached",
    );

    if (isAccessCodeLimitError) {
      const email = "dkenric@asu.edu";
      const subject = encodeURIComponent("Access Code Issue");
      const body = encodeURIComponent(
        `Hello, I'm having trouble with an access code for Psych For Life signup.\n\nAccess Code: ${accessCode ?? "N/A"}`,
      );

      const mailtoLink = `mailto:${email}?subject=${subject}&body=${body}`;

      return (
        <p className="text-sm leading-relaxed">
          There are no more sign-ins available with this code. If you believe
          you should be able to login, contact{" "}
          <a
            href={mailtoLink}
            className="hover:text-blue-500 underline transition-colors duration-200 font-medium"
            target="_blank"
            rel="noopener noreferrer"
          >
            {email}
          </a>
          .
        </p>
      );
    }

    const isUserExistsError = errorMessage.includes("User already exists");
    if (isUserExistsError) {
      return (
        <p className="text-sm">
          This username has been taken. Please try another name.
        </p>
      );
    }

    return <p className="text-sm">{errorMessage}</p>;
  };

  const signUpMutation = useSignUpUser({
    onSuccess: async (data: { status: string; message: string }) => {
      if (data.status === "SUCCESS") {
        notify(
          "success",
          "Account created successfully! Please verify your email.",
        );
        navigate("/verification-sent", {
          state: { email },
        });
      } else {
        setError(
          data.message || "An error occurred during signup. Please try again.",
        );
      }
    },
    onError: (error) => {
      // Handle backend error responses
      if (error && error.message) {
        setError(error.message);
      } else {
        setError("An error occurred during signup. Please try again.");
      }
    },
  });

  const isFormValid = () => {
    const missingFields = [];
    if (!username) missingFields.push("Username");
    if (!email) missingFields.push("Email");
    if (!password) missingFields.push("Password");
    if (!confirmPassword) missingFields.push("Confirm Password");
    if (!accessCode) missingFields.push("Access Code");

    if (missingFields.length > 0) {
      const fieldsText = missingFields.join(", ");
      const verb = missingFields.length > 1 ? "are" : "is";
      setError(`${fieldsText} ${verb} required`);
      return false;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return false;
    }

    if (!validateUsername(username)) {
      setError(
        "Please enter a valid username. It must be 1-128 characters and can only contain letters, numbers, underscore, hyphen, and dot.",
      );
      return false;
    }

    const passwordValidation = validatePassword(password);
    if (passwordValidation.isError) {
      setError(passwordValidation.errorMessage);
      return false;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return false;
    }
    if (!agreeToLegal) {
      setError(
        "You must agree to the Terms and Conditions, Privacy Policy and End User License Agreement",
      );
      return false;
    }
    return true;
  };

  const handleSignUp = async () => {
    setError("");

    if (!isFormValid()) {
      return;
    }

    signUpMutation.mutate({
      username,
      email,
      password,
      accessCode,
      legalAcceptance: {
        acceptLegal: agreeToLegal,
        version: legalDates?.termsVersion || "unknown",
        agreedToEmail:
          emailAccountIssues || emailFeedbackRequests || emailNewsUpdates,
      },
    });
  };

  return (
    <div className="min-h-screen relative pb-6 bg-gradient-to-b from-electrum-100 via-white to-white">
      {/* Fixed Background Image */}
      <div
        className="fixed inset-0 z-0 top-8"
        style={{
          backgroundImage: `url(${topicsBackground})`,
          backgroundSize: "cover",
          backgroundPosition: "center top",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "fixed",
        }}
      />
      <div className="relative">
        <div className="flex flex-col mx-auto w-full max-w-96 px-4 mt-16 space-y-6">
          <div className="bg-white/90 backdrop-blur-sm rounded-lg shadow-lg p-8">
            <div className="flex flex-col items-center mb-8">
              <img
                src={p4lLogo}
                alt="Psych For Life logo"
                className="w-32 h-32 mb-2"
              />
              <p className="text-gray-900 text-3xl">
                Psych For Life<sup>®</sup>
              </p>
            </div>
            <div className="space-y-6">
              <p className="text-gray-900 text-2xl font-bold">Sign Up</p>

              <div className="space-y-3">
                <p className="cursor-pointer text-sm text-gray-600 hover:text-gray-800">
                  Please enter the access code you received.
                </p>

                <TextField
                  label="Access Code"
                  variant="outlined"
                  fullWidth
                  name="accessCode"
                  value={accessCode}
                  onChange={(e) => {
                    setError("");
                    setAccessCode(e.target.value);
                  }}
                />
              </div>

              <TextField
                label="Username"
                variant="outlined"
                fullWidth
                type="username"
                name="username"
                value={username}
                onChange={(e) => {
                  setError("");
                  setUsername(e.target.value);
                }}
              />

              <TextField
                label="Email"
                variant="outlined"
                fullWidth
                type="email"
                name="email"
                value={email}
                onChange={(e) => {
                  setError("");
                  setEmail(e.target.value);
                }}
              />

              <div className="space-y-0">
                <TextField
                  label="Password"
                  variant="outlined"
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={password}
                  onChange={(e) => {
                    setError("");
                    setPassword(e.target.value);
                    setShowPasswordRequirements(e.target.value.length > 0);
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <PasswordRequirementsWarning
                  password={password}
                  showRequirements={showPasswordRequirements}
                />
              </div>

              <TextField
                label="Confirm Password"
                variant="outlined"
                fullWidth
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={confirmPassword}
                onChange={(e) => {
                  setError("");
                  setConfirmPassword(e.target.value);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreeToLegal}
                    onChange={(e) => {
                      setError("");
                      setAgreeToLegal(e.target.checked);
                    }}
                    color="primary"
                  />
                }
                label={
                  <span className="text-sm text-gray-700">
                    I agree to the{" "}
                    <button
                      type="button"
                      onClick={() => setShowTermsModal(true)}
                      className="text-blue-500 hover:text-blue-600 bg-transparent border-none cursor-pointer"
                    >
                      Terms and Conditions
                    </button>
                    ,{" "}
                    <button
                      type="button"
                      onClick={() => setShowPrivacyModal(true)}
                      className="text-blue-500 hover:text-blue-600 bg-transparent border-none cursor-pointer"
                    >
                      Privacy Policy
                    </button>{" "}
                    and{" "}
                    <button
                      type="button"
                      onClick={() => setShowEULAModal(true)}
                      className="text-blue-500 hover:text-blue-600 bg-transparent border-none cursor-pointer"
                    >
                      End User License Agreement
                    </button>
                    .
                  </span>
                }
              />

              <div className="w-full">
                <p className="text-sm text-gray-700 mb-2">
                  I agree to receive emails from Psych for Life about:
                </p>
                <div className="ml-4 space-y-1">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={emailAccountIssues}
                        onChange={(e) => {
                          setEmailAccountIssues(e.target.checked);
                        }}
                        color="primary"
                      />
                    }
                    label={
                      <span className="text-sm text-gray-700">
                        Issues with my account (e.g. security issues)
                      </span>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={emailFeedbackRequests}
                        onChange={(e) => {
                          setEmailFeedbackRequests(e.target.checked);
                        }}
                        color="primary"
                      />
                    }
                    label={
                      <span className="text-sm text-gray-700">
                        Requests for feedback to improve my experience with the
                        app
                      </span>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={emailNewsUpdates}
                        onChange={(e) => {
                          setEmailNewsUpdates(e.target.checked);
                        }}
                        color="primary"
                      />
                    }
                    label={
                      <span className="text-sm text-gray-700">
                        News and updates about the app
                      </span>
                    }
                  />
                </div>
                <p className="text-xs text-gray-600 italic mt-2">
                  We will only contact you when it is necessary or adds real
                  value to your experience.
                </p>
              </div>

              <Button
                className="w-full py-4 text-lg"
                onClick={handleSignUp}
                loading={signUpMutation.isPending}
              >
                Sign Up
              </Button>

              {error && (
                <div className="text-red-500 text-center">
                  {renderErrorMessage(error)}
                </div>
              )}

              <div className="text-center">
                <p className="text-sm text-gray-600">
                  Already have an account?{" "}
                  <Link
                    to="/login"
                    className="text-blue-500 hover:text-blue-600"
                  >
                    Log in
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <PrivacyPolicyModal
        open={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
      />

      <TermsOfServiceModal
        open={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />

      <EULAModal open={showEULAModal} onClose={() => setShowEULAModal(false)} />
    </div>
  );
};

export default SignupPage;
